import { CAMPAIGN_TYPES } from "@/components/campaign/ai/campaignTypes";
import { useBrandContext } from "@/contexts/brandContext";
import { useQuery } from "@apollo/client/react";
import useChatDocuments, {
  useIsGuestChat,
} from "@repo/components/src/hooks/agent/useChatDocuments";
import type { TurnPhase } from "@repo/components/src/shared/agent/turnReducer";
import type { AgentToolCall } from "@repo/components/src/shared/agent/types";
import { Badge, BadgeIcon, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import { HStack } from "@repo/components/src/ui/hstack";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { AgentKey } from "@repo/graphql/gql/graphql";
import { Link } from "expo-router";
import { Info, Pencil } from "lucide-react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import { EMPTY_DRAFT_PREVIEW } from "../draftPreviewTypes";
import { parseDraftPreview } from "../parseDraftPreview";
import AboutSection from "./AboutSection";
import BrandRow from "./BrandRow";
import DeliverablesSection from "./DeliverablesSection";
import HeaderBlock from "./HeaderBlock";
import Hero from "./Hero";
import { usePostTypeLookup } from "./postTypeLookup";
import ProductsSection from "./ProductsSection";
import { deriveSectionStatuses, DRAFT_WRITERS } from "./sectionStatus";
import TermsSection from "./TermsSection";

type Props = {
  brandId: string;
  /** The draft this chat is filling — `session.subjectId`. Absent until the first run. */
  draftId: string | undefined;
  tools: AgentToolCall[];
  turnPhase: TurnPhase;
  /** `session.agentKey` — which of the three campaign creators is running this
   * chat, used only to read the "UGC"/"Orgánica"/"MeliClips" eyebrow label. */
  agentKey: string | undefined;
  /**
   * Bumped when the draft changed without a tool call saying so — a form-first card
   * wrote it, or the script landed. Refetches the draft each time it changes.
   */
  refreshKey?: number;
};

const MANUAL_DRAFT_ROUTE = {
  pathname: "/app/(brands)/[brandId]/(main)/campaign/new/manual",
} as const;

/**
 * The campaign taking shape, next to the conversation building it — as a faithful
 * replica of what the creator actually sees in the mobile app once this draft
 * becomes a real campaign (`apps/mobil/.../campaign/[campaign].tsx`), rather than
 * an accordion of form-shaped summaries.
 *
 * Moment 4 of the animation system ("Draft creado", built in the chat itself)
 * points here now: a small "Editar" link sits next to the PREVISUALIZACIÓN
 * badge for anyone who wants to jump into the manual form. The "Publicar
 * campaña" action itself lives on that chat card now, not here.
 */
const CreatorPreview = ({
  brandId,
  draftId,
  tools,
  turnPhase,
  agentKey,
  refreshKey = 0,
}: Props) => {
  const { currency } = useBrandContext();
  // The manual wizard is behind a login: a start with no account has no "Editar".
  const guest = useIsGuestChat();
  const documents = useChatDocuments();
  const { data, refetch } = useQuery(documents.campaignDraft, {
    variables: { id: draftId ?? "" },
    skip: !draftId,
    fetchPolicy: "cache-and-network",
  });

  // Refetch as each write lands, not once at the end of the turn — see the
  // previous `campaignPreview` implementation this replaces for the full
  // reasoning; the machinery itself is unchanged.
  const refetched = useRef<Set<string>>(new Set());

  useEffect(() => {
    refetched.current = new Set();
  }, [draftId]);

  useEffect(() => {
    const landed = tools.filter(
      (tool) =>
        tool.done &&
        DRAFT_WRITERS.has(tool.toolName) &&
        !refetched.current.has(tool.toolCallId),
    );
    if (!landed.length || !draftId) return;
    for (const tool of landed) refetched.current.add(tool.toolCallId);
    void refetch();
  }, [tools, refetch, draftId]);

  useEffect(() => {
    if (!refreshKey || !draftId) return;
    void refetch();
  }, [refreshKey, refetch, draftId]);

  const [awaitingTurnRefetch, setAwaitingTurnRefetch] = useState(false);
  const prevTurnPhase = useRef<TurnPhase>(turnPhase);

  useEffect(() => {
    const enteredSettling =
      turnPhase === "settling" && prevTurnPhase.current !== "settling";
    prevTurnPhase.current = turnPhase;
    if (!enteredSettling || !draftId) return;
    setAwaitingTurnRefetch(true);
    void refetch().finally(() => setAwaitingTurnRefetch(false));
  }, [turnPhase, refetch, draftId]);

  const draft = data?.campaignDraft.get;
  const model = useMemo(
    () =>
      draft?.draftData
        ? parseDraftPreview(draft.draftData)
        : EMPTY_DRAFT_PREVIEW,
    [draft],
  );
  const imageUrls = useMemo(
    () =>
      (draft?.medias ?? [])
        .map((media) => media.media?.url)
        .filter((url): url is string => !!url),
    [draft],
  );
  const statuses = useMemo(
    () =>
      deriveSectionStatuses(draft?.missing ?? [], tools, awaitingTurnRefetch),
    [draft?.missing, tools, awaitingTurnRefetch],
  );

  const campaignTypeLabel = CAMPAIGN_TYPES.find(
    (type) => type.agentKey === (agentKey as AgentKey),
  )?.label;

  const postTypes = usePostTypeLookup();

  // One batch query by `id_In` for every product this preview could possibly
  // show — the main products AND the canje products — rather than one request
  // per section.
  const productIds = useMemo(() => {
    const ids = new Set<string>(model.productIds);
    for (const ref of model.priceProductIds) ids.add(ref.productId);
    return Array.from(ids);
  }, [model.productIds, model.priceProductIds]);

  const { data: productsData } = useQuery(documents.productsSelection, {
    variables: { id_In: productIds },
    skip: productIds.length === 0,
  });
  const products = useMemo(
    () =>
      new Map(
        (productsData?.product.list.edges ?? []).map((edge) => [
          edge.node.id,
          edge.node,
        ]),
      ),
    [productsData],
  );

  if (!draftId || !draft) {
    return (
      <VStack space="md">
        <HStack>
          <Badge variant="solid" action="success" size="md">
            <BadgeText>PREVISUALIZACIÓN</BadgeText>
            <BadgeIcon as={Info} className="ml-2" />
          </Badge>
        </HStack>
        <Text className="text-xs text-typography-400">
          Se irá completando a medida que conversas con el asistente.
        </Text>
      </VStack>
    );
  }

  return (
    <VStack space="md">
      <HStack className="items-center justify-between">
        <Badge variant="solid" action="success" size="md">
          <BadgeText>PREVISUALIZACIÓN</BadgeText>
          <BadgeIcon as={Info} className="ml-2" />
        </Badge>
        {guest ? null : (
          <Link
            href={{ ...MANUAL_DRAFT_ROUTE, params: { brandId, draftId } }}
            asChild
          >
            <Button action="brand" variant="outline" size="xs">
              <ButtonIcon as={Pencil} />
              <ButtonText>Editar</ButtonText>
            </Button>
          </Link>
        )}
      </HStack>

      <Hero imageUrls={imageUrls} />
      <BrandRow />
      <HeaderBlock
        campaignTypeLabel={campaignTypeLabel}
        contents={model.contents}
        postTypes={postTypes}
        title={model.name}
      />

      <Box>
        <AboutSection description={model.description} />
      </Box>
      <Box>
        <ProductsSection
          object={model.object}
          productIds={model.productIds}
          products={products}
          status={statuses.products}
        />
      </Box>
      <Box>
        <DeliverablesSection
          contents={model.contents}
          postTypes={postTypes}
          status={statuses.contents}
        />
      </Box>
      {currency ? (
        <Box>
          <TermsSection
            model={model}
            products={products}
            status={statuses.terms}
            currencyCode={currency}
          />
        </Box>
      ) : null}
    </VStack>
  );
};

export default CreatorPreview;
